import { AnimatePresence, motion } from "framer-motion"
import { AlertTriangle, Loader2, X } from "lucide-react"
import { useCallback, useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { useSystemNoticeSocket } from "../hooks/SystemNotice"

const STATUS_URL = "/admin/stats/status"

const formatRemaining = (sec) => {
  const pad = (n) => String(n).padStart(2, "0")
  return `${pad(Math.floor(sec / 60))}:${pad(sec % 60)}`
}

const SystemNoticeBanner = () => {
  const { t } = useTranslation()

  const [notice, setNotice] = useState(null)
  const [phase, setPhase] = useState("idle") // idle | countdown | executing
  const [now, setNow] = useState(Date.now())
  const [dismissed, setDismissed] = useState(false)

  const handleScheduled = useCallback((n) => {
    if (!n) return
    // поправка на разницу часов клиента и сервера
    const offset = Date.now() - n.server_now
    setNotice({ action: n.action, endsAt: n.ends_at + offset, initiatedBy: n.initiated_by })
    setPhase("countdown")
    setDismissed(false)
  }, [])

  const handleExecuting = useCallback((data) => {
    setNotice((prev) => prev ?? { action: data.action })
    setPhase("executing")
  }, [])

  useSystemNoticeSocket({ onScheduled: handleScheduled, onExecuting: handleExecuting })

  useEffect(() => {
    if (phase !== "countdown") return
    const id = setInterval(() => setNow(Date.now()), 250)
    return () => clearInterval(id)
  }, [phase])

  useEffect(() => {
    if (phase !== "executing") return
    let cancelled = false
    let intervalId = null

    const startId = setTimeout(() => {
      intervalId = setInterval(async () => {
        try {
          const res = await fetch(STATUS_URL, { cache: "no-store" })
          if (res.ok && !cancelled) window.location.reload()
        } catch (_) {}
      }, 3000)
    }, 5000)

    return () => {
      cancelled = true
      clearTimeout(startId)
      if (intervalId) clearInterval(intervalId)
    }
  }, [phase])

  if (phase === "idle" || !notice) return null
  if (phase === "countdown" && dismissed) return null

  const isRestart = notice.action === "restart"
  const remaining = Math.max(0, Math.ceil(((notice.endsAt ?? 0) - now) / 1000))
  const urgent = phase === "executing" || remaining <= 10

  return (
    <AnimatePresence>
      <motion.div
        key="system-notice"
        initial={{ y: 80, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 80, opacity: 0 }}
        className={`fixed bottom-4 right-4 left-4 sm:left-auto sm:w-96 z-[9999] flex items-start gap-3 rounded-xl px-4 py-3 text-white shadow-2xl ${
          urgent ? "bg-red-600" : "bg-amber-500"
        }`}
      >
        {phase === "executing" ? (
          <>
            <Loader2 className="w-5 h-5 mt-0.5 animate-spin flex-shrink-0" />
            <span className="text-sm font-semibold">
              {isRestart
                ? t(
                    "admin.system_notice_restarting",
                    "Идёт перезапуск системы. Страница обновится автоматически."
                  )
                : t(
                    "admin.system_notice_updating",
                    "Идёт обновление системы. Страница обновится автоматически."
                  )}
            </span>
          </>
        ) : (
          <>
            <AlertTriangle className="w-5 h-5 mt-0.5 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold">
                {isRestart
                  ? t(
                      "admin.system_notice_restart",
                      "Система будет перезапущена. Сохраните несохранённые данные."
                    )
                  : t(
                      "admin.system_notice_update",
                      "Система будет обновлена. Сохраните несохранённые данные."
                    )}
              </div>
              <div className="mt-2 inline-block font-mono text-lg font-bold tabular-nums bg-black/20 rounded px-2 py-0.5">
                {formatRemaining(remaining)}
              </div>
            </div>
            <button
              onClick={() => setDismissed(true)}
              className="p-1 rounded hover:bg-black/20 transition-colors flex-shrink-0"
            >
              <X className="w-4 h-4" />
            </button>
          </>
        )}
      </motion.div>
    </AnimatePresence>
  )
}

export default SystemNoticeBanner
